/**
 * Google sign-in for the browser, via Google Identity Services (GIS).
 * Ported from garage-buddy-web.
 *
 * Access tokens live ~1 hour. GIS can only fetch one through a popup, and
 * browsers block popups that no click opened, so a "silent" request on page
 * load fails and every reload asked the user to sign in again. The token is
 * therefore kept in localStorage until it expires: a reload or a second tab
 * within the hour starts signed in, and after that one click renews it. The
 * only third-party script on the page is Google's own sign-in client (see the
 * CSP in public/_headers), which issues the token in the first place.
 *
 * Scopes differ from Garage Buddy: Finance Buddy reads files the user drops
 * into its folder from the Drive UI or phone, which `drive.file` cannot see,
 * so it asks for full `drive`. `drive.appdata` holds the Claude key;
 * `gmail.send` sends the digest. See ARCHITECTURE.md, OAuth scopes.
 */
import type { TokenClient, TokenResponse } from './gsi'

export const SCOPES = [
  'https://www.googleapis.com/auth/drive',
  'https://www.googleapis.com/auth/drive.appdata',
  'https://www.googleapis.com/auth/gmail.send',
  'openid',
  'email',
  'profile',
].join(' ')

/** Renew this many ms before the token actually expires. */
const RENEW_MARGIN_MS = 60_000

/**
 * Deadline for a SILENT token request.
 *
 * GIS answers a live session in a few hundred milliseconds. When there is no
 * session to renew from it can simply never call back — neither `callback` nor
 * `error_callback` fires, because the frame it polls is cross-origin and
 * Cross-Origin-Opener-Policy stops it observing the outcome. Without a ceiling
 * the sign-in promise never settles and the app sits on its loading spinner
 * forever: what a signed-out visitor, an expired session, or a browser that
 * blocks third-party cookies (Safari by default) would all have seen.
 *
 * Generous enough for a slow mobile connection, short enough that nobody
 * stares at a spinner wondering whether the page is broken.
 */
const SILENT_TIMEOUT_MS = 6_000

/** Remembering the last account lets silent renewal skip the chooser. */
const HINT_KEY = 'fb.accountHint'
const TOKEN_KEY = 'fb.token'

function store(key: string, value: string | undefined) {
  try {
    if (value === undefined) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    // Private mode or blocked storage: sign-in still works, it just isn't remembered.
  }
}

export class AuthError extends Error {
  constructor(message: string, readonly kind: 'not_signed_in' | 'popup_blocked' | 'denied' | 'unknown' = 'unknown') {
    super(message)
    this.name = 'AuthError'
  }
}

export interface GoogleProfile {
  email: string
  name?: string
  picture?: string
}

/** Resolves once the GIS script tag in index.html has finished loading. */
function gisReady(): Promise<NonNullable<Window['google']>> {
  if (window.google) return Promise.resolve(window.google)
  return new Promise((resolve, reject) => {
    const started = Date.now()
    const tick = () => {
      if (window.google) return resolve(window.google)
      if (Date.now() - started > 15_000) {
        return reject(new AuthError('Could not load Google sign-in. Check your network or an ad blocker.'))
      }
      setTimeout(tick, 50)
    }
    tick()
  })
}

export class GoogleAuth {
  private client?: TokenClient
  private token?: string
  private expiresAt = 0
  /** In-flight renewal, shared so concurrent API calls don't stack popups. */
  private pending?: Promise<string>
  private resolvePending?: (token: string) => void
  private rejectPending?: (err: Error) => void

  profile?: GoogleProfile

  get isSignedIn(): boolean {
    return !!this.token && Date.now() < this.expiresAt
  }

  /** The last account used, if any — shown before a silent renewal completes. */
  get hint(): string | undefined {
    try {
      return localStorage.getItem(HINT_KEY) ?? undefined
    } catch {
      return undefined
    }
  }

  /** A token saved by an earlier page load that still has time left. */
  private loadSaved(): boolean {
    try {
      const saved = JSON.parse(localStorage.getItem(TOKEN_KEY) ?? 'null') as { token: string; expiresAt: number } | null
      if (saved && Date.now() < saved.expiresAt - RENEW_MARGIN_MS) {
        this.token = saved.token
        this.expiresAt = saved.expiresAt
        return true
      }
    } catch {
      // Unreadable: fall through to a fresh request.
    }
    return false
  }

  private async ensureClient(): Promise<TokenClient> {
    if (this.client) return this.client
    const google = await gisReady()
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
    if (!clientId) {
      throw new AuthError(
        'No Google client id configured. Set VITE_GOOGLE_CLIENT_ID in .env.',
      )
    }
    this.client = google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: SCOPES,
      callback: (response: TokenResponse) => {
        if (response.access_token) {
          this.token = response.access_token
          this.expiresAt = Date.now() + (response.expires_in ?? 3600) * 1000
          store(TOKEN_KEY, JSON.stringify({ token: this.token, expiresAt: this.expiresAt }))
          this.resolvePending?.(response.access_token)
        } else {
          this.rejectPending?.(
            new AuthError(response.error_description || response.error || 'Sign-in failed', 'denied'),
          )
        }
        this.clearPending()
      },
      error_callback: (error) => {
        // `popup_failed_to_open` is the ad-blocker / blocked-popup case;
        // `user_logged_out` means there is no live Google session to renew from.
        const kind = error.type === 'popup_failed_to_open' ? 'popup_blocked' : 'not_signed_in'
        this.rejectPending?.(new AuthError(error.message || 'Sign-in was dismissed', kind))
        this.clearPending()
      },
    })
    return this.client
  }

  private clearPending() {
    this.pending = undefined
    this.resolvePending = undefined
    this.rejectPending = undefined
  }

  /**
   * Requests a token. `interactive: false` attempts a silent renewal and
   * fails fast if the browser would have to show UI — that is what we try on
   * page load, so a returning user never sees a login screen.
   */
  private async request(interactive: boolean): Promise<string> {
    if (this.pending) return this.pending
    const client = await this.ensureClient()

    const answered = new Promise<string>((resolve, reject) => {
      this.resolvePending = resolve
      this.rejectPending = reject
    })

    // Only the silent path gets a deadline. An interactive request is waiting
    // on a human typing a password — there is no sensible time limit on that.
    let deadline: ReturnType<typeof setTimeout> | undefined
    if (!interactive) {
      deadline = setTimeout(() => {
        this.rejectPending?.(
          new AuthError('Google did not answer the silent sign-in attempt', 'not_signed_in'),
        )
        this.clearPending()
      }, SILENT_TIMEOUT_MS)
    }
    // A late callback arriving after the deadline is harmless: `clearPending`
    // has dropped the resolvers, so it only refreshes the cached token.
    this.pending = answered.finally(() => clearTimeout(deadline))

    client.requestAccessToken(
      interactive
        ? { prompt: this.hint ? '' : 'consent', hint: this.hint }
        : { prompt: 'none', hint: this.hint },
    )
    return this.pending
  }

  /** A valid access token, renewing silently if the current one is stale. */
  async accessToken(): Promise<string> {
    if (this.token && Date.now() < this.expiresAt - RENEW_MARGIN_MS) return this.token
    const token = await this.request(false)
    await this.loadProfile()
    return token
  }

  /** Explicit sign-in — may open Google's account chooser / consent screen. */
  async signIn(): Promise<GoogleProfile> {
    await this.request(true)
    return this.loadProfile()
  }

  /** Silent restore on page load. Resolves undefined when a prompt is needed. */
  async restore(): Promise<GoogleProfile | undefined> {
    try {
      if (!this.loadSaved()) await this.request(false)
      return await this.loadProfile()
    } catch {
      return undefined
    }
  }

  private async loadProfile(): Promise<GoogleProfile> {
    if (this.profile) return this.profile
    const response = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
      headers: { Authorization: `Bearer ${this.token}` },
    })
    if (!response.ok) {
      // A saved token Google no longer honours (revoked, signed out elsewhere).
      this.invalidate()
      throw new AuthError('Could not read your Google profile')
    }
    const json = (await response.json()) as { email: string; name?: string; picture?: string }
    this.profile = { email: json.email, name: json.name, picture: json.picture }
    store(HINT_KEY, json.email)
    return this.profile
  }

  /** Drops the token here and at Google, so the next sign-in starts clean. */
  async signOut(): Promise<void> {
    const token = this.token
    this.token = undefined
    this.expiresAt = 0
    this.profile = undefined
    store(HINT_KEY, undefined)
    store(TOKEN_KEY, undefined)
    if (token) {
      const google = await gisReady().catch(() => undefined)
      google?.accounts.oauth2.revoke(token)
    }
  }

  /** Forces the next `accessToken()` to renew — used after a 401. */
  invalidate() {
    this.expiresAt = 0
    store(TOKEN_KEY, undefined)
  }
}

export const auth = new GoogleAuth()
